import { Component, Inject, OnDestroy, OnInit, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { DownloadDocumentAction, GetDocumentsByAmcoIdAction, UploadDocumentAction, UploadMultipleDocumentsAction, ViewDocumentAction, UpdateDocumentTypeAction, GetDocumentTypesAction, ClearDocumentsAction } from '@ngxs-store/customs-store/customs.actions';
import { DocumentTypeDialogComponent } from '../document-type-dialog/document-type-dialog.component';
import { Select, Store } from '@ngxs/store';
import { CustomsState } from '@ngxs-store/customs-store/customs.state';
import { Observable, Subject, combineLatest } from 'rxjs';
import { DocumentsModel } from '@core/services/api.service';
import { takeUntil, tap, filter, map } from 'rxjs/operators';
import { Actions, ofActionSuccessful } from '@ngxs/store';
import { OpenSnackbarAction } from '@ngxs-store/ui-store/ui.actions';
import { SnackbarType } from '@core/models';
import { AuthState } from '@ngxs-store/auth-store/auth.state';
import { DecodedToken } from '@core/models/auth/decoded-token';
import { UploadMultipleDocumentsSuccessAction } from '@ngxs-store/customs-store/customs.actions';

export enum DocumentType {
    POD = 'POD',
    ClearanceDocuments = 'Commercial Docs',
    EADCERT = 'EAD',
    IMPORTMRN = 'IMPORT',
    EXSCERT = 'EXS',
    TRANSITFILED = 'T1/T2/LRN'
}

const documentTypeToApiValue: { [key: string]: string } = {
    'Commercial Docs': 'COMMDOCS',
    'POD': 'POD',
    'EAD': 'EAD',
    'IMPORT': 'IMPORT',
    'EXS': 'EXS',
    'T1/T2/LRN': 'T1/T2/LRN'
};

const apiValueToDisplayValue: { [key: string]: string } = {
    'COMMDOCS': 'Commercial Docs',
    'POD': 'POD',
    'EADCERT': 'EAD',
    'IMPORTMRN': 'IMPORT',
    'EXSCERT': 'EXS',
    'T1/T2/LRN': 'T1/T2/LRN'
};

interface DocumentItem {
  no: number;
  name: string;
  dateCreated: Date;
  type: string;
  size: string;
  documentType?: string;
  fileTypeId?: number;
  createdBy?: string;
  isOptimistic?: boolean;
}

interface UploadingFile {
  name: string;
  size: string;
  file: File;
  documentType?: string;
  processing?: boolean;
  completed?: boolean;
}

@Component({
  selector: 'app-view-documents-dialog',
  templateUrl: './view-documents-dialog.html',
  styleUrls: ['./view-documents-dialog.scss']
})
export class ViewDocumentsDialogComponent implements OnInit, OnDestroy {
  @Select(CustomsState.getDocuments) documents$: Observable<DocumentsModel[]>;
  @Select(CustomsState.getIsLoadingDocuments) isLoading$: Observable<boolean>;
  @Select(CustomsState.getDocumentTypes) documentTypes$: Observable<any[]>;
  @Select(AuthState.getDecodedToken) decodedToken$: Observable<DecodedToken>;
  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef<HTMLInputElement>;

  private destroy$ = new Subject<void>();

  documents: DocumentItem[] = [];
  displayedColumns: string[] = ['no', 'name', 'dateCreated', 'type', 'documentType', 'createdBy', 'size', 'actions'];
  uploadingFiles: UploadingFile[] = [];
  amcoId: number;
  Id: number;
  consignmentNumber: string;
  documentTypes = Object.values(DocumentType);
  lastUploadedFileInfo: any = null;
  optimisticDocuments: DocumentItem[] = []; // Track optimistic documents
  constructor(
    public dialogRef: MatDialogRef<ViewDocumentsDialogComponent>,
    private dialog: MatDialog,
    private store: Store,
    private cdr: ChangeDetectorRef,
    private actions$: Actions,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit() {
    this.amcoId = this.data.amcoId;
    this.Id = this.data.id;
    this.consignmentNumber = this.data.consignmentNumber;
    
    this.store.dispatch(new GetDocumentsByAmcoIdAction(this.amcoId));
    this.store.dispatch(new GetDocumentTypesAction(this.amcoId));

    // Handle preloaded files from Front plugin
    if (this.data.preloadedFiles && Array.isArray(this.data.preloadedFiles)) {
      console.log('Dialog received preloadedFiles:', this.data.preloadedFiles.map(f => ({
        name: f?.name,
        size: f?.size,
        type: f?.type,
        isFile: f instanceof File,
        isBlob: f instanceof Blob
      })));
      
      // Add Front plugin files directly without recreating them
      this.data.preloadedFiles.forEach((file: File) => {
        if (file instanceof File && file.size > 0) {
          this.uploadingFiles.push({
            name: file.name,
            size: this.formatFileSize(file.size),
            file: file  // Use the file directly, it's already a proper File object
          });
        }
      });
      
      this.cdr.detectChanges();
    }

    // Monitor documents state changes for debugging
    this.documents$.pipe(
      takeUntil(this.destroy$),
      tap(documents => {
        console.log('Documents state updated:', documents);
      })
    ).subscribe();

    // Combine documents and document types observables and map the data
            combineLatest([this.documents$, this.documentTypes$])
      .pipe(
        takeUntil(this.destroy$),
        filter(([documents, types]) => documents !== null && types !== null),
        tap(([documents, types]) => {
          console.log('Combined Documents and Types:', documents, types);
        })
      )
      .subscribe(([documents, types]) => {
        if (documents) {
          let mappedDocs = documents.map((doc, index) => {
            let documentType;
            let fileTypeId: number | undefined;

            const matchingType = types?.find(type => type.fileName === doc.name);
            if (matchingType) {
              fileTypeId = matchingType.fileTypeId;

              if (matchingType.description) {
                // Convert API value to display value for showing in UI
                const apiValue = matchingType.description.toUpperCase();
                documentType = apiValueToDisplayValue[apiValue] || matchingType.description;
              }
            }

            return {
              name: doc.name,
              type: doc.type,
              size: doc.size,
              no: index + 1,
              dateCreated: doc.createdDate,
              createdDate: doc.createdDate,
              documentType,
              fileTypeId,
              createdBy: matchingType?.createdBy || ''
            };
          });

          // Merge optimistic documents that are not in backend data
          const backendNames = new Set(mappedDocs.map(d => d.name));
          const stillOptimistic = this.optimisticDocuments.filter(
            doc => !backendNames.has(doc.name)
          );

          // Remove optimistic documents that now exist in backend data
          this.optimisticDocuments = stillOptimistic;

          // Add optimistic documents to the end of the list
          this.documents = [...mappedDocs, ...stillOptimistic.map((doc, idx) => ({
            ...doc,
            no: mappedDocs.length + idx + 1,
            isOptimistic: true // Ensure optimistic flag is preserved
          }))];

          this.cdr.detectChanges();
        }
      });

    this.actions$.pipe(
      ofActionSuccessful(UploadDocumentAction),
      tap(() => {
        this.store.dispatch(new OpenSnackbarAction({ message: 'Document uploaded successfully!', type: SnackbarType.Simple }));

        // Optimistically add the new document to the table
        if (this.lastUploadedFileInfo) {
          this.optimisticDocuments.push({
            ...this.lastUploadedFileInfo,
            no: this.documents.length + this.optimisticDocuments.length + 1,
          });

          // Mark the corresponding file as completed
          const fileIndex = this.uploadingFiles.findIndex(f => f.name === this.lastUploadedFileInfo.name);
          if (fileIndex !== -1) {
            this.uploadingFiles[fileIndex].completed = true;
            this.uploadingFiles[fileIndex].processing = false;

            // Remove completed files after a short delay
            setTimeout(() => {
              const completedIndex = this.uploadingFiles.findIndex(f => f.name === this.lastUploadedFileInfo.name);
              if (completedIndex !== -1 && this.uploadingFiles[completedIndex].completed) {
                this.uploadingFiles.splice(completedIndex, 1);
                this.cdr.detectChanges();
              }
            }, 2000);
          }

          this.lastUploadedFileInfo = null;
          this.cdr.detectChanges();
        }
      }),
      takeUntil(this.destroy$)
    ).subscribe();

    // Handle multiple document upload success
    this.actions$.pipe(
      ofActionSuccessful(UploadMultipleDocumentsAction),
      tap(() => {
        this.store.dispatch(new OpenSnackbarAction({ message: 'Documents uploaded successfully!', type: SnackbarType.Simple }));

        // Mark all processing files as completed
        this.uploadingFiles.forEach((file, index) => {
          if (file.processing) {
            this.uploadingFiles[index].completed = true;
            this.uploadingFiles[index].processing = false;
          }
        });

        // Remove optimistic documents that were just uploaded
        const uploadedFileNames = this.uploadingFiles
          .filter(f => f.completed)
          .map(f => f.name);

        this.optimisticDocuments = this.optimisticDocuments.filter(
          doc => !uploadedFileNames.includes(doc.name)
        );

        // Remove completed files after a short delay
        setTimeout(() => {
          this.uploadingFiles = this.uploadingFiles.filter(f => !f.completed);
          this.cdr.detectChanges();
        }, 2000);

        this.cdr.detectChanges();
      }),
      takeUntil(this.destroy$)
    ).subscribe();

    // Handle multiple document upload success from state
    this.actions$.pipe(
      ofActionSuccessful(UploadMultipleDocumentsSuccessAction),
      tap(() => {
        console.log('UploadMultipleDocumentsSuccessAction received - documents should be updated');
        // The state has already been updated, just ensure UI reflects changes
        this.cdr.detectChanges();
      }),
      takeUntil(this.destroy$)
    ).subscribe();

    this.actions$.pipe(
      ofActionSuccessful(UpdateDocumentTypeAction),
      tap(() => {
        this.store.dispatch(new OpenSnackbarAction({ message: 'Document type updated successfully!', type: SnackbarType.Simple }));
        // Refresh both documents and document types after update
        this.store.dispatch(new GetDocumentsByAmcoIdAction(this.amcoId));
        this.store.dispatch(new GetDocumentTypesAction(this.amcoId));
        this.cdr.detectChanges();
      }),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  onFileDropped(event: any) {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      this.handleFiles(Array.from(files));
    }
  }

  onFileSelected(event: any) {
    const files = event.target.files;
    if (files.length > 0) {
      this.handleFiles(Array.from(files));
    }
    // Reset the file input value to prevent caching issues
    event.target.value = '';
  }

  handleFiles(files: File[]) {
    files.forEach(file => {
      if (!(file instanceof File)) {
        console.error('Invalid file object:', file);
        return;
      }

      if (file.size === 0) {
        console.error('File is empty:', file.name);
        return;
      }

      const validFile = new File([file], file.name, {
        type: file.type || 'application/octet-stream'
      });

      this.uploadingFiles.push({
        name: validFile.name,
        size: this.formatFileSize(validFile.size),
        file: validFile
      });
    });
  }

  async selectCategoryForFile(index: number) {
    const file = this.uploadingFiles[index];
    if (!file) return;

    const dialogRef = this.dialog.open(DocumentTypeDialogComponent, {
      width: '400px',
      disableClose: true,
      data: { fileName: file.name }
    });

    const result = await dialogRef.afterClosed().toPromise();
    if (result) {
      this.uploadingFiles[index].documentType = result;
    }
  }

  async uploadFile(index: number) {
    const file = this.uploadingFiles[index];
    if (!file || !file.documentType) return;

    if (file.file.size === 0) {
      console.error('Cannot upload empty file');
      return;
    }

    // Mark file as processing
    this.uploadingFiles[index].processing = true;

    // Store info for optimistic update
    this.lastUploadedFileInfo = {
      name: file.file.name,
      size: file.size,
      type: file.file.type,
      dateCreated: new Date(),
      documentType: file.documentType,
      fileTypeId: undefined,
      createdBy: this.store.selectSnapshot(AuthState.getDecodedToken)?.username || ''
    };

    // Convert display value to API value before sending
    const apiDocumentType = documentTypeToApiValue[file.documentType] || file.documentType;

    // Use individual upload for single files
    this.store.dispatch(new UploadDocumentAction(
      this.Id,
      file.file,
      apiDocumentType,
      this.amcoId
    ));
  }

  uploadAllFiles() {
    const filesToUpload = this.uploadingFiles.filter(f => f.documentType && !f.completed);

    // Validate that we have files with valid document types
    if (filesToUpload.length === 0) {
      console.warn('No files ready for upload');
      return;
    }

    const files = filesToUpload.map(f => f.file);
    // Convert display values to API values before sending
    const documentTypes = filesToUpload.map(f => {
      const displayType = f.documentType;
      return documentTypeToApiValue[displayType] || displayType;
    }).filter(type => type && type.trim() !== '');

    // Double-check that we have valid document types
    if (documentTypes.length !== files.length) {
      console.error('Some files are missing document types');
      return;
    }

    console.log('Uploading files:', files.map(f => f.name));
    console.log('Document types (API values):', documentTypes);

    // Create optimistic documents for immediate display
    const optimisticDocs = filesToUpload.map((file, index) => ({
      name: file.file.name,
      size: file.size,
      type: file.file.type,
      no: this.documents.length + this.optimisticDocuments.length + index + 1,
      dateCreated: new Date(),
      createdDate: new Date(),
      documentType: file.documentType,
      fileTypeId: undefined,
      createdBy: this.store.selectSnapshot(AuthState.getDecodedToken)?.username || '',
      isOptimistic: true // Flag to identify optimistic documents
    }));

    // Add optimistic documents to the table immediately
    this.optimisticDocuments.push(...optimisticDocs);
    this.documents = [...this.documents, ...optimisticDocs];
    this.cdr.detectChanges();

    this.store.dispatch(new UploadMultipleDocumentsAction(
      this.Id,
      files,
      documentTypes,
      this.amcoId
    ));

    // Mark files as processing
    filesToUpload.forEach(file => {
      const index = this.uploadingFiles.indexOf(file);
      if (index !== -1) {
        this.uploadingFiles[index].processing = true;
      }
    });
  }

  removeFile(index: number) {
    this.uploadingFiles.splice(index, 1);
  }

  clearAllFiles() {
    this.uploadingFiles = [];
  }

  hasFilesReadyForUpload(): boolean {
    return this.uploadingFiles.some(f => f.documentType && !f.completed);
  }

  getFilesReadyCount(): number {
    return this.uploadingFiles.filter(f => f.documentType && !f.completed).length;
  }

  hasFilesWithoutCategory(): boolean {
    return this.uploadingFiles.some(f => !f.documentType);
  }

  isUploading(): boolean {
    return this.uploadingFiles.some(f => f.processing);
  }

  isOptimisticDocument(doc: DocumentItem): boolean {
    return doc.isOptimistic === true;
  }

  selectFiles(event: Event) {
    event.stopPropagation();
    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.click();
    }
  }

  async setCategoryForAllFiles() {
    const filesWithoutCategory = this.uploadingFiles.filter(f => !f.documentType);
    if (filesWithoutCategory.length === 0) return;

    const dialogRef = this.dialog.open(DocumentTypeDialogComponent, {
      width: '400px',
      disableClose: true,
      data: { fileName: `Multiple files (${filesWithoutCategory.length})` }
    });

    const result = await dialogRef.afterClosed().toPromise();
    if (result) {
      this.uploadingFiles.forEach(file => {
        if (!file.documentType) {
          file.documentType = result;
        }
      });
      this.cdr.detectChanges();
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + 'KB';
    return (bytes / (1024 * 1024)).toFixed(1) + 'MB';
  }

  downloadDocument(document: DocumentItem) {
    this.store.dispatch(new DownloadDocumentAction(this.amcoId, document.name));
}

  viewDocument(document: DocumentItem) {
    this.store.dispatch(new ViewDocumentAction(this.amcoId, document.name));
  }

  onClose() {
    this.store.dispatch(new ClearDocumentsAction());

    this.documents = [];
    this.optimisticDocuments = [];
    this.lastUploadedFileInfo = null;
    this.uploadingFiles = [];

    this.dialogRef.close();
  }

  ngOnDestroy() {
    this.store.dispatch(new ClearDocumentsAction());

    this.destroy$.next();
    this.destroy$.complete();
  }

  async editDocumentType(document: DocumentItem) {
    const dialogRef = this.dialog.open(DocumentTypeDialogComponent, {
      width: '400px',
      disableClose: true,
      data: { fileName: document.name, currentType: document.documentType }
    });

    const result = await dialogRef.afterClosed().toPromise();
    if (result) {
      // Ensure amcoId is a number
      const amcoId = Number(this.Id);
      if (isNaN(amcoId)) {
        console.error('Invalid amcoId:', this.Id);
        return;
      }

      // Convert display value to API value before sending
      const apiDocumentType = documentTypeToApiValue[result] || result;

      this.store.dispatch(new UpdateDocumentTypeAction(
        amcoId,
        document.name,
        apiDocumentType
      ));
    }
  }
}
